import { useState } from 'react';
import {
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useSetChapterAmbientAudio } from '../../api/queries/voices';

interface AmbientAudioSelectorProps {
  chapterId: string;
  currentAmbientUrl?: string | null;
  onSelect: (url: string | null) => void;
}

interface AmbientPreset {
  id: string;
  label: string;
  description?: string;
  url: string | null;
}

const AMBIENT_PRESETS: AmbientPreset[] = [
  { id: 'none', label: 'None', url: null },
  {
    id: 'forest',
    label: 'Forest & Birds',
    description: 'Gentle forest sounds with birdsong',
    url: '/ambient/forest.mp3',
  },
  {
    id: 'rain',
    label: 'Rain',
    description: 'Steady rainfall on leaves',
    url: '/ambient/rain.mp3',
  },
  {
    id: 'city',
    label: 'City Streets',
    description: 'Distant traffic and urban ambiance',
    url: '/ambient/city.mp3',
  },
  {
    id: 'ocean',
    label: 'Ocean Waves',
    description: 'Rhythmic waves on shore',
    url: '/ambient/ocean.mp3',
  },
  {
    id: 'tavern',
    label: 'Tavern',
    description: 'Crackling fire, murmured conversation',
    url: '/ambient/tavern.mp3',
  },
  {
    id: 'battle',
    label: 'Battle',
    description: 'Distant clashing swords, war drums',
    url: '/ambient/battle.mp3',
  },
  {
    id: 'night',
    label: 'Night',
    description: 'Crickets, wind, occasional owl',
    url: '/ambient/night.mp3',
  },
  {
    id: 'thunder',
    label: 'Thunderstorm',
    description: 'Rolling thunder and heavy rain',
    url: '/ambient/thunder.mp3',
  },
];

function findPresetByUrl(url: string | null | undefined): string {
  if (!url) return 'none';
  const match = AMBIENT_PRESETS.find((p) => p.url === url);
  return match?.id ?? 'none';
}

/**
 * Per-chapter ambient audio selector. Offers preset ambient soundscapes
 * and a placeholder for custom ambient generation.
 */
export function AmbientAudioSelector({
  chapterId,
  currentAmbientUrl,
  onSelect,
}: AmbientAudioSelectorProps) {
  const [selected, setSelected] = useState(() =>
    findPresetByUrl(currentAmbientUrl),
  );
  const [snackOpen, setSnackOpen] = useState(false);
  const saveAmbient = useSetChapterAmbientAudio(chapterId);

  const handleChange = (presetId: string) => {
    setSelected(presetId);
    const preset = AMBIENT_PRESETS.find((p) => p.id === presetId);
    const url = preset?.url ?? null;
    onSelect(url);
    saveAmbient.mutate(url);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MusicNoteIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Ambient Audio</Typography>
        </Stack>

        <RadioGroup
          value={selected}
          onChange={(e) => handleChange(e.target.value)}
        >
          {AMBIENT_PRESETS.map((preset) => (
            <FormControlLabel
              key={preset.id}
              value={preset.id}
              control={<Radio size="small" />}
              label={
                <Stack>
                  <Typography variant="body2">{preset.label}</Typography>
                  {preset.description && (
                    <Typography variant="caption" color="text.secondary">
                      {preset.description}
                    </Typography>
                  )}
                </Stack>
              }
            />
          ))}
        </RadioGroup>

        <Button
          size="small"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => setSnackOpen(true)}
        >
          Generate custom
        </Button>
      </Stack>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message="Custom ambient generation coming soon"
      />
    </Paper>
  );
}
